/**
 * Перенос `data/chat_repository_api.dart`.
 *
 * Riverpod в порте нет, поэтому `chatRepositoryProvider` заменён на
 * [chatRepository] — один общий экземпляр поверх общего `httpClient`.
 *
 * Запись читалась в Dart в байты (`audioFile.readAsBytes()`), потому что
 * `MultipartFile.fromBytes` иначе не собрать. В браузере `File` — это уже
 * `Blob`, и `FormData` отправляет его сам: читать заранее незачем, тело
 * уходит потоком.
 */

import { TabletSessionManager } from '@/data/tabletSessionManager';
import type { AppLanguage } from '@/domain/models/appLanguage';
import type { ConfigModel } from '@/domain/models/configModel';
import { personCopyWith, type PersonModel } from '@/domain/models/personModel';
import type { SttConfigModel } from '@/domain/models/sttConfigModel';
import type { ChatRepository, GetTranscriptionParams } from '@/domain/repository/chatRepository';
import { AppException, stackTraceOf } from '@/utils/exceptions/appException';
import { AppFailure, FailureKind, FailureOp } from '@/utils/exceptions/appFailure';
import { fileNameWithExtension } from '@/utils/extensions/fileExtension';
import { httpClient, type HttpClient } from '@/utils/http/httpClient';
import { asRecord } from '@/utils/http/types';
import { Nullable } from '@/utils/nullable';
import { StorageHelper } from '@/utils/storageHelper';

/**
 * `_defaultModel`. Bifrost без `model` отвечает 400; `auto` — шлюз сам
 * выбирает модель по языку (uz/en/ar → nemo, ru → gigaam). Значение из
 * админки важнее.
 */
const DEFAULT_MODEL = 'auto';

/**
 * `_timeout`. Загрузка WAV и распознавание длинной фразы не укладываются
 * в базовые 10 с клиента; шлюз ждёт сервис до 300 с.
 */
const TIMEOUT_MS = 60_000;

/** Нижняя граница клиентских ошибок: с 4xx сервис не принял запрос. */
const HTTP_CLIENT_ERROR_MIN = 400;

/** Нижняя граница серверных ошибок: 5xx — не про устаревшие ключи. */
const HTTP_SERVER_ERROR_MIN = 500;

/** `e.statusCode ?? 0` — кода нет (сеть, формат ответа). */
const UNKNOWN_STATUS = 0;

/** Аргументы конструктора [ChatRepositoryApi]. */
export interface ChatRepositoryApiInit {
  readonly client: HttpClient;
}

/** Аргументы [ChatRepositoryApi.transcriptionUrl]. */
export interface TranscriptionUrlParams {
  readonly person: PersonModel;
  readonly config?: ConfigModel | null;
}

/** Аргументы `_postTranscription`. */
interface PostTranscriptionParams {
  readonly endpoint: string;
  readonly model: string | null;
  readonly apiKey: string | null;
  readonly audioFile: File;
  readonly filename: string;
  readonly language: AppLanguage;
}

/** Аргументы повтора после отказа своего STT-сервиса. */
interface RetryWithFreshSttParams {
  readonly error: AppException;
  readonly ownService: boolean;
  readonly audioFile: File;
  readonly filename: string;
  readonly language: AppLanguage;
  readonly personSlug: string;
}

/** Поля multipart-запроса распознавания. */
interface TranscriptionFormParams {
  readonly audioFile: File;
  readonly filename: string;
  readonly language: AppLanguage;
  readonly model: string | null;
}

export class ChatRepositoryApi implements ChatRepository {
  constructor({ client }: ChatRepositoryApiInit) {
    this.#client = client;
  }

  readonly #client: HttpClient;

  /**
   * Адрес распознавания речи: у аватара (`persons[].stt.api_url`, новый
   * бекенд), иначе общий `transcribe_url` конфигурации (прод 0.3.9).
   * `null` — не настроено нигде.
   */
  static transcriptionUrl({ person, config }: TranscriptionUrlParams): string | null {
    return nonEmpty(person.stt?.apiUrl) ?? nonEmpty(config?.transriptionsUrl);
  }

  async getTranscription({
    audioFile,
    language,
    person,
  }: GetTranscriptionParams): Promise<string> {
    const config = StorageHelper.instance.config;
    // Персона чата — снимок на момент входа. Ключ и адрес могли обновиться
    // с тех пор (перечитывание после 401, тап по языку) — берём сохранённые.
    const current = personCopyWith(person, {
      stt: new Nullable(storedStt(config, person.slug) ?? person.stt),
    });
    const stt = current.stt;
    const endpoint = ChatRepositoryApi.transcriptionUrl({ person: current, config });
    if (endpoint === null) {
      throw new AppFailure({
        op: FailureOp.transcribe,
        kind: FailureKind.notConfigured,
      });
    }
    const ownService = nonEmpty(stt?.apiUrl) !== null;
    const filename = fileNameWithExtension(audioFile);
    try {
      return await this.#postTranscription({
        endpoint,
        // Модель и ключ — только своему сервису аватара, общий их не ждёт.
        model: ownService ? (nonEmpty(stt?.model) ?? DEFAULT_MODEL) : null,
        apiKey: ownService ? nonEmpty(stt?.apiKey) : null,
        audioFile,
        filename,
        language,
      });
    } catch (e) {
      if (!(e instanceof AppException)) throw AppException.handle(e, stackTraceOf(e));
      return await this.#retryWithFreshStt({
        error: e,
        ownService,
        audioFile,
        filename,
        language,
        personSlug: person.slug,
      });
    }
  }

  /**
   * Свой сервис не принял запрос (4xx): сохранённые STT устарели —
   * админ мог сменить ключ (401/403), адрес или модель (400/404).
   * Один раз перечитываем защищённую конфигурацию и повторяем уже с её
   * значениями; они же сохраняются для следующих записей.
   */
  async #retryWithFreshStt({
    error,
    ownService,
    audioFile,
    filename,
    language,
    personSlug,
  }: RetryWithFreshSttParams): Promise<string> {
    const status = error.statusCode ?? UNKNOWN_STATUS;
    const rejected = status >= HTTP_CLIENT_ERROR_MIN && status < HTTP_SERVER_ERROR_MIN;
    if (!ownService || !rejected) throw AppException.handle(error, stackTraceOf(error));
    const baseUrl = StorageHelper.instance.baseUrl;
    const fresh =
      baseUrl === null
        ? null
        : await TabletSessionManager.instance.refreshSttFor({ baseUrl, personSlug });
    const freshUrl = nonEmpty(fresh?.apiUrl);
    if (fresh === null || freshUrl === null) {
      throw AppException.handle(error, stackTraceOf(error));
    }
    return this.#postTranscription({
      endpoint: freshUrl,
      model: nonEmpty(fresh.model) ?? DEFAULT_MODEL,
      apiKey: nonEmpty(fresh.apiKey),
      audioFile,
      filename,
      language,
    });
  }

  async #postTranscription({
    endpoint,
    model,
    apiKey,
    audioFile,
    filename,
    language,
  }: PostTranscriptionParams): Promise<string> {
    try {
      const response = await this.#client.post(endpoint, {
        sendTimeout: TIMEOUT_MS,
        receiveTimeout: TIMEOUT_MS,
        headers:
          apiKey === null
            ? null
            : {
                // Основной заголовок — как у OpenAI-совместимых сервисов,
                // его же принимает Bifrost (llm.iccu.uz). x-bf-vk —
                // собственный заголовок шлюза, дублируем на случай, если
                // Authorization на нём отключат; лишний сервис игнорирует.
                Authorization: `Bearer ${apiKey}`,
                'x-bf-vk': apiKey,
              },
        data: transcriptionForm({ audioFile, filename, language, model }),
      });
      return transcriptionText(response.data);
    } catch (e) {
      throw AppException.handle(e, stackTraceOf(e));
    }
  }
}

/**
 * `FormData.fromMap({...})` из Dart. Объект строится новый, существующий
 * нигде не правится.
 */
function transcriptionForm({
  audioFile,
  filename,
  language,
  model,
}: TranscriptionFormParams): FormData {
  const form = new FormData();
  // `MultipartFile.fromBytes(bytes, filename: …)` — в браузере файл уходит
  // как есть, имя передаётся третьим аргументом `append`.
  form.append('file', audioFile, filename);
  // `language` — контракт Bifrost: без него шлюз берёт uz и для
  // русского выбирает не ту модель. `language_code` ждёт общий
  // transcribe_url прода. Лишнее поле сервисы игнорируют.
  //
  // `language.toString()` в Dart возвращал `param`; здесь значением
  // перечисления служит сам `param`, так что строка та же.
  form.append('language', language);
  form.append('language_code', language);
  if (model !== null) form.append('model', model);
  return form;
}

/**
 * Текст из ответа STT. Общий `transcribe_url` прода отвечает
 * `{"transcription": …}`, OpenAI-совместимый сервис аватара (Bifrost на
 * llm.iccu.uz) — `{"text": …}`. Ни того, ни другого — ответ не по
 * контракту: `SyntaxError` (замена дартовому `FormatException`: слой
 * исключений разбирает его так же), чтобы наружу ушёл STT-FORMAT, а не
 * ошибка приведения типа.
 */
function transcriptionText(body: unknown): string {
  const map = asRecord(body);
  if (map !== null) {
    const text = map['transcription'] ?? map['text'];
    if (typeof text === 'string') return text;
  }
  throw new SyntaxError(
    `в ответе STT нет ни transcription, ни text: ${runtimeTypeOf(body)}`,
  );
}

/** `body.runtimeType` из Dart — в JS ближайшее к нему имя конструктора. */
function runtimeTypeOf(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  const name = (value as { constructor?: { name?: string } }).constructor?.name;
  return name ?? typeof value;
}

/**
 * `_storedStt`. STT персоны из сохранённой конфигурации; матчинг по `slug`,
 * как при слиянии ключей. `null` — персоны там нет.
 */
function storedStt(config: ConfigModel | null, slug: string): SttConfigModel | null {
  if (config === null) return null;
  for (const stored of config.persons) {
    if (stored.slug === slug) return stored.stt;
  }
  return null;
}

/** `_nonEmpty`: `null`, пустая строка и одни пробелы — это «нет значения». */
function nonEmpty(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? null;
  return trimmed === null || trimmed.length === 0 ? null : trimmed;
}

/** Аналог `chatRepositoryProvider`: репозиторий на общем клиенте. */
export const chatRepository: ChatRepository = new ChatRepositoryApi({
  client: httpClient,
});
